<script lang="ts" module>
	export type WhiteBorderImage = {
		width: number;
		height: number;
		url: string;
	};
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { cssVariables } from '../../utils/cssVariables/cssVariables';
	import { getDownscaledImageSrcset } from '../../utils/getDownscaledImageSrcset/getDownscaledImageSrcset';
	import { getImageAspectRatio } from '../../utils/getImageAspectRatio/getImageAspectRatio';

	let {
		alt,
		classes = {},
		dataCy,
		image,
		loading = 'lazy',
		sizes = '100vw',
		whiteBorderFix = true,
		onclick,
		cardImagePadding,
		cardImageBgColor,
		...rest
	}: Props = $props();

	let style = $derived(
		(() => {
			let styleProps = '';

			if (cardImagePadding !== null) {
				styleProps += `padding: ${cardImagePadding}px;`;
			}

			if (cardImageBgColor) {
				styleProps += `background-color: ${cardImageBgColor};`;
			}

			return styleProps;
		})()
	);

	interface Props {
		cardImagePadding?: number | null;
		cardImageBgColor?: string | null;
		alt: string | undefined | null;
		classes?: { border?: string; image?: string };
		dataCy: string;
		loading?: 'eager' | 'lazy';
		/** `sizes` for the image's srcset; the default never picks a smaller image than `image.url`'s on a large screen. */
		sizes?: string;
		image: WhiteBorderImage;
		whiteBorderFix?: boolean | undefined;
		class?: string;
		onclick?: () => void;
		style?: string;
	}

	let clientWidth: number | undefined = $state();
	let clientHeight: number | undefined = $state();

	let imageAspectRatio = $derived(
		getImageAspectRatio({
			width: image?.width,
			height: image?.height
		})
	);

	let containerAspectRatio = $derived(
		getImageAspectRatio({
			width: clientWidth,
			height: clientHeight
		})
	);

	let dataCyPrefix = $derived(`${dataCy}-image-container`);
</script>

<!-- eslint-disable-next-line svelte/valid-compile -->
<div
	{...onclick ? { onclick, role: 'link' } : {}}
	data-cy={dataCyPrefix}
	{style}
	bind:clientWidth
	bind:clientHeight
	class={twMerge(
		classNames('flex items-center justify-center bg-[#efefef] p-2', {
			invisible: !clientWidth
		}),
		rest.class
	)}
	use:cssVariables={{
		imageAspectRatio
	}}
>
	<div
		class={classNames('relative bg-cover', {
			'h-auto w-full': containerAspectRatio <= imageAspectRatio,
			'h-full w-auto': containerAspectRatio > imageAspectRatio
		})}
		style="aspect-ratio: var(--imageAspectRatio)"
	>
		<img
			src={image?.url}
			srcset={getDownscaledImageSrcset(image?.url)}
			{sizes}
			class={twMerge('h-full w-full', classes?.image)}
			{alt}
			{loading}
			decoding="async"
		/>

		{#if whiteBorderFix}
			<div
				class={twMerge(
					'absolute bottom-0 left-0 right-0 top-0 border-2 border-[#efefef] text-[#efefef]',
					classes.border
				)}
				style={cardImageBgColor ? `border-color: ${cardImageBgColor};` : ''}
			></div>
		{/if}
	</div>
</div>
